import type { AuthErrorCode } from './types.ts'

/** A failure of the auth flow itself, not an API error. `message` is English and for
 *  developers (docs/design/frontend-v1.md §4.5): the UI branches on `code` and never prints it. */
export class AuthError extends Error {
  readonly code: AuthErrorCode
  /** The server said the grant is spent or revoked (`invalid_grant`): no retry can help. A network
   *  failure, a 5xx or any other refusal is not this, and the refresh token may still work. */
  readonly terminal: boolean

  constructor(
    code: AuthErrorCode,
    message: string,
    options?: ErrorOptions & { readonly terminal?: boolean },
  ) {
    super(message, options)
    this.name = 'AuthError'
    this.code = code
    this.terminal = options?.terminal === true
  }
}

/** Refusals raised before any request leaves: the package answered by itself, so no retry
 *  can change the outcome and a caller's retry policy has to treat them as an answer. */
const LOCAL_REFUSALS: readonly AuthErrorCode[] = ['no_refresh_token', 'scope_insufficient']

export function isLocalAuthRefusal(error: unknown): boolean {
  return error instanceof AuthError && LOCAL_REFUSALS.includes(error.code)
}
